import { Archive, Mail, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import {
  archiverCompte,
  archiverComptes,
  listerArchives,
  listerComptes,
  modifierCompte,
  purgerArchives,
  restaurerCompte,
  type CompteAdmin,
  type ListeArchives,
  type ListeComptes,
  type ResultatLot,
} from '../api/admin';
import type { Role } from '../api/auth';
import { creerLienReinitialisation } from '../api/motdepasse';
import { listerTechniciens, type Technicien } from '../api/techniciens';
import {
  listerInvitations,
  renouvelerInvitation,
  revoquerInvitation,
  revoquerInvitations,
  type InvitationAdmin,
  type ResultatLotInvitations,
} from '../api/invitations';
import { useSession } from '../app/SessionContext';
import { ModaleInvitation } from './ModaleInvitation';
import { ModaleTechnicien } from './ModaleTechnicien';

/** Ce que l'écran sait afficher. `donnees` survit au rechargement. */
interface Etat {
  readonly donnees?: ListeComptes;
  readonly chargement: boolean;
  readonly erreur?: string;
}

const ROLES: ReadonlyArray<{ valeur: Role; libelle: string }> = [
  { valeur: 'prestataire', libelle: 'Prestataire' },
  { valeur: 'admin', libelle: 'Administrateur' },
];

/** Les onglets : le modèle en donnait trois, le quatrième sépare les invitations closes. */
type Onglet = 'comptes' | 'invitations' | 'invitations-servies' | 'archives';

/** Accorde un nom et son participe : « 1 compte archivé », « 3 comptes archivés ». */
function accorder(nombre: number, singulier: string, pluriel: string): string {
  return `${nombre} ${nombre > 1 ? pluriel : singulier}`;
}

type Ensemble = readonly string[];

function basculer(ensemble: Ensemble, id: string): Ensemble {
  return ensemble.includes(id) ? ensemble.filter((autre) => autre !== id) : [...ensemble, id];
}

export function Administration() {
  const { utilisateur } = useSession();
  const [etat, setEtat] = useState<Etat>({ chargement: true });
  const [onglet, setOnglet] = useState<Onglet>('comptes');
  const [confirmation, setConfirmation] = useState<string>();
  const [erreurAction, setErreurAction] = useState<string>();
  /** Compte dont une écriture est en cours : la ligne est désactivée, pas tout l'écran. */
  const [enCours, setEnCours] = useState<string>();
  /** Comptes cochés : la sélection est locale à l'onglet affiché. */
  const [selectionComptes, setSelectionComptes] = useState<Ensemble>([]);
  const [selectionInvitations, setSelectionInvitations] = useState<Ensemble>([]);
  /** La purge est définitive : on la fait armer AVANT de l'exécuter. */
  const [purgeArmee, setPurgeArmee] = useState(false);
  // Invitations
  /** Invitations ENCORE utilisables — liste filtrée par le serveur. */
  const [invitations, setInvitations] = useState<readonly InvitationAdmin[]>([]);
  /** Invitations closes (servies ou périmées) — autre filtre du serveur. */
  const [invitationsServies, setInvitationsServies] = useState<readonly InvitationAdmin[]>([]);
  /** Compteurs des onglets : arrêtés par le SERVEUR, jamais recomptés ici. */
  const [compteursInvitations, setCompteursInvitations] = useState({ enAttente: 0, servies: 0 });
  /** La modale « Invitation simple » (modèle fourni) remplace le panneau en ligne. */
  const [modaleInvitation, setModaleInvitation] = useState(false);
  const [lienGenere, setLienGenere] = useState<string>();
  const [erreurInvitation, setErreurInvitation] = useState<string>();
  // Archives
  const [archives, setArchives] = useState<ListeArchives>();
  // Fiches techniciens
  const [modale, setModale] = useState(false);
  const [techniciens, setTechniciens] = useState<readonly Technicien[]>([]);
  /** Lien de réinitialisation fraîchement fabriqué : affiché une fois. */
  const [lienReinit, setLienReinit] = useState<string>();

  const charger = useCallback(async (signal: AbortSignal) => {
    setEtat((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
    const resultat = await listerComptes({ signal });
    if (resultat.ok && resultat.donnees) {
      setEtat({ donnees: resultat.donnees, chargement: false });
    } else {
      setEtat((precedent) => ({
        ...precedent,
        chargement: false,
        erreur: resultat.message ?? 'Erreur inattendue.',
      }));
    }
  }, []);

  const chargerTechniciens = useCallback(async (signal?: AbortSignal) => {
    const resultat = await listerTechniciens({ signal });
    if (resultat.ok && resultat.donnees) setTechniciens(resultat.donnees.items);
  }, []);

  const chargerInvitations = useCallback(async (signal?: AbortSignal) => {
    const options = signal ? { signal } : {};
    // Deux filtres, un seul état à l'écran : c'est le SERVEUR qui décide ce qui
    // entre dans chaque onglet, et les compteurs portent sur l'ensemble.
    const [attente, closes] = await Promise.all([
      listerInvitations({ ...options, etat: 'en_attente' }),
      listerInvitations({ ...options, etat: 'servies' }),
    ]);
    if (attente.ok && attente.donnees) {
      setInvitations(attente.donnees.items);
      setCompteursInvitations({
        enAttente: attente.donnees.enAttente,
        servies: attente.donnees.servies,
      });
    }
    if (closes.ok && closes.donnees) setInvitationsServies(closes.donnees.items);
  }, []);

  const chargerArchives = useCallback(async (signal?: AbortSignal) => {
    const resultat = await listerArchives(signal ? { signal } : {});
    if (resultat.ok && resultat.donnees) {
      const liste = resultat.donnees;
      setArchives(liste);
      // Une ligne cochée qui vient d'être archivée quitte la sélection : garder son
      // identifiant ferait agir « la sélection » sur un compte qui n'est plus là.
      const archivesIds = new Set(liste.items.map((compte) => compte.id));
      setSelectionComptes((selection) => selection.filter((id) => !archivesIds.has(id)));
    }
  }, []);

  useEffect(() => {
    const controleur = new AbortController();
    void charger(controleur.signal);
    void chargerInvitations(controleur.signal);
    void chargerArchives(controleur.signal);
    void chargerTechniciens(controleur.signal);
    return () => controleur.abort();
  }, [charger, chargerInvitations, chargerArchives, chargerTechniciens]);

  const rafraichirComptes = useCallback(async () => {
    await charger(new AbortController().signal);
    await chargerArchives();
  }, [charger, chargerArchives]);

  const reinitialiser = async (compte: CompteAdmin) => {
    setErreurAction(undefined);
    setConfirmation(undefined);
    setEnCours(compte.id);

    const resultat = await creerLienReinitialisation(compte.id);
    setEnCours(undefined);

    if (resultat.ok && resultat.donnees) {
      // Le lien n'est rendu qu'ici : on l'affiche pour copie et transmission.
      setLienReinit(`${globalThis.location.origin}${resultat.donnees.lien}`);
      setConfirmation(
        `Lien de réinitialisation créé pour ${resultat.donnees.email} (valable jusqu’au ${resultat.donnees.expireLe.slice(0, 16)}).`,
      );
    } else {
      setErreurAction(resultat.message ?? 'Création du lien impossible.');
    }
  };

  const revoquer = async (invitation: InvitationAdmin) => {
    setErreurInvitation(undefined);
    const resultat = await revoquerInvitation(invitation.id);
    if (resultat.ok) {
      await chargerInvitations();
    } else {
      setErreurInvitation(resultat.message ?? 'Révocation impossible.');
    }
  };

  /** « Renvoyer » : même invitation, jeton neuf. L'ancien lien meurt aussitôt. */
  const renvoyer = async (invitation: InvitationAdmin) => {
    setErreurInvitation(undefined);
    setConfirmation(undefined);
    setEnCours(invitation.id);

    const resultat = await renouvelerInvitation(invitation.id);
    setEnCours(undefined);

    if (resultat.ok && resultat.donnees) {
      setLienGenere(`${globalThis.location.origin}${resultat.donnees.lien}`);
      setConfirmation(
        `Invitation renvoyée à ${resultat.donnees.invitation.email} : nouveau lien valable jusqu’au ${resultat.donnees.invitation.expireLe.slice(0, 10)}. L’ancien ne fonctionne plus.`,
      );
      await chargerInvitations();
    } else {
      setErreurInvitation(resultat.message ?? 'Renvoi impossible.');
    }
  };

  const ecrire = useCallback(
    async (compte: CompteAdmin, changement: { role: Role }, message: string) => {
      setEnCours(compte.id);
      setErreurAction(undefined);
      setConfirmation(undefined);

      const resultat = await modifierCompte(compte.id, changement);
      setEnCours(undefined);

      if (resultat.ok && resultat.donnees) {
        // L'état affiché est celui que le serveur renvoie, jamais celui qu'on espérait.
        setConfirmation(
          `${message} — ${resultat.donnees.email} : ${resultat.donnees.roleLibelle}.`,
        );
        void rafraichirComptes();
      } else {
        setErreurAction(resultat.message ?? 'Modification refusée.');
      }
    },
    [rafraichirComptes],
  );

  /** Le compte rendu d'un lot vient du serveur : l'écran le met en forme, point. */
  const resumeLot = (
    sujet: { genre: string; singulier: string; pluriel: string },
    reponse: {
      faits: number;
      refuses: number;
      resultats: readonly (ResultatLot | ResultatLotInvitations)[];
    },
  ): { message: string; succes: boolean } => {
    const refus = reponse.resultats.filter((resultat) => !resultat.ok);
    const motif = (resultat: ResultatLot | ResultatLotInvitations): string =>
      `${resultat.message ?? 'refus'}`;
    if (reponse.refuses === 0) {
      return {
        message: `${accorder(reponse.faits, `${sujet.genre} ${sujet.singulier}`, `${sujet.genre}s ${sujet.pluriel}`)}.`,
        succes: true,
      };
    }
    return {
      message: `${accorder(reponse.faits, `${sujet.genre} ${sujet.singulier}`, `${sujet.genre}s ${sujet.pluriel}`)}, ${accorder(reponse.refuses, 'refusé', 'refusés')} — ${refus
        .map(motif)
        .join(' · ')}`,
      // Un refus partiel reste une réussite partielle : on l'affiche en vert,
      // avec le motif des refus. Un lot entièrement refusé, lui, est une alerte.
      succes: reponse.faits > 0,
    };
  };

  /** Archive un compte : il part dans l'onglet « Archivés » et se restaure. */
  const archiver = async (compte: CompteAdmin) => {
    setEnCours(compte.id);
    setErreurAction(undefined);
    setConfirmation(undefined);

    const resultat = await archiverCompte(compte.id);
    setEnCours(undefined);

    if (resultat.ok && resultat.donnees) {
      setConfirmation(
        archives?.retentionJours === undefined
          ? `${resultat.donnees.email} archivé.`
          : `${resultat.donnees.email} archivé — restaurable pendant ${archives.retentionJours} jours.`,
      );
      await rafraichirComptes();
    } else {
      setErreurAction(resultat.message ?? 'Archivage refusé.');
    }
  };

  const archiverSelection = async () => {
    if (selectionComptes.length === 0) return;

    setErreurAction(undefined);
    setConfirmation(undefined);
    setEnCours('lot');

    // Un SEUL appel : le serveur applique la règle à chaque identifiant et rend
    // le sort de chacun. L'écran n'ordonne rien et ne décide de rien.
    const resultat = await archiverComptes(selectionComptes);
    setEnCours(undefined);
    setSelectionComptes([]);

    if (resultat.ok && resultat.donnees) {
      const { message, succes } = resumeLot(
        { genre: 'compte', singulier: 'archivé', pluriel: 'archivés' },
        resultat.donnees,
      );
      if (succes) {
        setConfirmation(message);
      } else {
        setErreurAction(message);
      }
    } else {
      setErreurAction(resultat.message ?? 'Archivage refusé.');
    }
    await rafraichirComptes();
  };

  const revoquerSelection = async () => {
    if (selectionInvitations.length === 0) return;

    setErreurInvitation(undefined);
    setEnCours('lot');
    const resultat = await revoquerInvitations(selectionInvitations);
    setEnCours(undefined);
    setSelectionInvitations([]);

    if (resultat.ok && resultat.donnees) {
      const { message, succes } = resumeLot(
        { genre: 'invitation', singulier: 'révoquée', pluriel: 'révoquées' },
        resultat.donnees,
      );
      if (succes) {
        setConfirmation(message);
      } else {
        setErreurInvitation(message);
      }
    } else {
      setErreurInvitation(resultat.message ?? 'Révocation impossible.');
    }
    await chargerInvitations();
  };

  const restaurer = async (compte: CompteAdmin) => {
    setEnCours(compte.id);
    setErreurAction(undefined);
    setConfirmation(undefined);

    const resultat = await restaurerCompte(compte.id);
    setEnCours(undefined);

    if (resultat.ok && resultat.donnees) {
      setConfirmation(`${resultat.donnees.email} restauré : le compte est de nouveau actif.`);
      await rafraichirComptes();
    } else {
      setErreurAction(resultat.message ?? 'Restauration impossible.');
    }
  };

  const purger = async () => {
    setErreurAction(undefined);
    setConfirmation(undefined);
    setEnCours('purge');

    const resultat = await purgerArchives();
    setEnCours(undefined);
    setPurgeArmee(false);

    if (resultat.ok) {
      const effacees = resultat.donnees?.purges ?? 0;
      setConfirmation(
        resultat.donnees?.message ??
          `${accorder(effacees, 'archive effacée', 'archives effacées')} définitivement, avec leur portefeuille.`,
      );
      await rafraichirComptes();
    } else {
      setErreurAction(resultat.message ?? 'Purge impossible.');
    }
  };

  const donnees = etat.donnees;
  const moi = utilisateur?.id;
  const nombreEnAttente = compteursInvitations.enAttente;
  const retention = archives?.retentionJours;
  const retentionTexte = retention === undefined ? 'la durée de rétention' : `${retention} jours`;
  const echues = archives?.echues ?? 0;

  const onglets: ReadonlyArray<{ cle: Onglet; libelle: string; compte: number }> = [
    { cle: 'comptes', libelle: 'Comptes actifs', compte: donnees?.total ?? 0 },
    { cle: 'invitations', libelle: 'Invitations en attente', compte: nombreEnAttente },
    {
      cle: 'invitations-servies',
      libelle: 'Invitations servies',
      compte: compteursInvitations.servies,
    },
    { cle: 'archives', libelle: 'Archivés', compte: archives?.total ?? 0 },
  ];
  /** Archives échues : la purge attend une décision humaine, on le signale. */
  const alertePurge = (archives?.echues ?? 0) > 0;

  /** Tous les comptes cochables de l'onglet : le sien ne l'est jamais. */
  const cochablesComptes = (donnees?.items ?? []).filter((compte) => compte.id !== moi);
  const cochablesInvitations = invitations.filter((invitation) => invitation.valide);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <p className="text-sm text-slate-600">
          Les comptes de la plateforme. Vous voyez le <strong>volume</strong> de clients de chacun,
          jamais leur contenu : l’isolation entre prestataires reste entière.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setModaleInvitation(true);
              setLienGenere(undefined);
              setErreurInvitation(undefined);
            }}
            className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 whitespace-nowrap"
          >
            Invitation simple
          </button>
          <button
            type="button"
            onClick={() => setModale(true)}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 whitespace-nowrap flex items-center gap-2"
          >
            <UserPlus aria-hidden="true" className="w-4 h-4" />
            Ajouter un technicien
          </button>
        </div>
      </div>

      <ModaleInvitation
        ouvert={modaleInvitation}
        onFermer={() => setModaleInvitation(false)}
        onCree={() => void chargerInvitations()}
      />

      <ModaleTechnicien
        ouvert={modale}
        onFermer={() => setModale(false)}
        onCree={() => void chargerTechniciens()}
      />

      {lienReinit && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" role="status">
          <p className="text-sm font-medium text-slate-800 mb-1">Lien de réinitialisation</p>
          <p className="text-xs text-slate-500 mb-2">
            Transmettez-le au titulaire : il ne sera plus affiché, et il ne sert qu’une fois.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={lienReinit}
              aria-label="Lien de réinitialisation"
              onFocus={(evenement) => evenement.target.select()}
              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 mono"
            />
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(lienReinit)}
              className="px-3 py-2 border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50"
            >
              Copier
            </button>
          </div>
        </div>
      )}

      {lienGenere && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" role="status">
          <p className="text-sm font-medium text-slate-800 mb-1">Lien d’invitation</p>
          <p className="text-xs text-slate-500 mb-2">
            Il ne sera plus affiché : copiez-le maintenant et transmettez-le au technicien.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={lienGenere}
              aria-label="Lien d’invitation"
              onFocus={(evenement) => evenement.target.select()}
              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 mono"
            />
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(lienGenere)}
              className="px-3 py-2 border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50"
            >
              Copier
            </button>
          </div>
        </div>
      )}

      {confirmation && (
        <p role="status" className="text-sm text-green-700">
          {confirmation}
        </p>
      )}

      {erreurAction && (
        <p role="alert" className="text-sm text-red-700">
          {erreurAction}
        </p>
      )}

      {/* ── Onglets : le compte vient du SERVEUR, jamais d'un total recopié ── */}
      <div
        role="tablist"
        aria-label="Comptes, invitations, archives"
        className="flex items-center gap-2 border-b border-slate-200"
      >
        {onglets.map((entree) => {
          const actif = onglet === entree.cle;
          return (
            <button
              key={entree.cle}
              type="button"
              role="tab"
              id={`onglet-${entree.cle}`}
              aria-selected={actif}
              aria-controls={`panneau-${entree.cle}`}
              onClick={() => setOnglet(entree.cle)}
              className={
                actif
                  ? 'px-5 py-2.5 text-sm font-medium text-slate-800 border-b-2 border-slate-800 -mb-px flex items-center gap-2'
                  : 'px-5 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-800 border-b-2 border-transparent -mb-px flex items-center gap-2'
              }
            >
              {entree.libelle}
              <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
                {entree.compte}
              </span>
              {entree.cle === 'archives' && alertePurge && (
                <span className="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                  {echues} à purger
                </span>
              )}
            </button>
          );
        })}
      </div>

      {onglet === 'comptes' && (
        <div
          id="panneau-comptes"
          role="tabpanel"
          aria-labelledby="onglet-comptes"
          className="space-y-4"
        >
          {selectionComptes.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <p className="text-sm text-blue-800 font-medium">
                {accorder(selectionComptes.length, 'compte sélectionné', 'comptes sélectionnés')}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectionComptes([])}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50"
                >
                  Tout désélectionner
                </button>
                <button
                  type="button"
                  onClick={() => void archiverSelection()}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
                >
                  Archiver la sélection
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {etat.erreur && (
              <div className="p-6 border-b border-slate-100" role="alert">
                <p className="font-medium text-red-700 mb-1">Comptes indisponibles</p>
                <p className="text-sm text-slate-600 mb-4">{etat.erreur}</p>
                <button
                  type="button"
                  onClick={() => void charger(new AbortController().signal)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
                >
                  Réessayer
                </button>
              </div>
            )}

            {!donnees && etat.chargement && !etat.erreur && (
              <p className="p-6 text-sm text-slate-500">Chargement des comptes…</p>
            )}

            {donnees && (
              <div
                className="overflow-x-auto transition-opacity"
                aria-busy={etat.chargement}
                style={etat.chargement ? { opacity: 0.5 } : undefined}
              >
                <table className="w-full text-sm text-left">
                  <caption className="sr-only">
                    Comptes de la plateforme, avec leur rôle, leur état et leur nombre de clients
                  </caption>
                  <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                    <tr>
                      <th scope="col" className="px-6 py-4 w-12">
                        <input
                          type="checkbox"
                          aria-label="Sélectionner tous les comptes"
                          checked={
                            cochablesComptes.length > 0 &&
                            cochablesComptes.every((compte) => selectionComptes.includes(compte.id))
                          }
                          onChange={(evenement) =>
                            setSelectionComptes(
                              evenement.target.checked
                                ? cochablesComptes.map((compte) => compte.id)
                                : [],
                            )
                          }
                          className="w-4 h-4 rounded border-slate-300 cursor-pointer"
                        />
                      </th>
                      <th scope="col" className="px-6 py-4">
                        Compte
                      </th>
                      <th scope="col" className="px-6 py-4">
                        Rôle
                      </th>
                      <th scope="col" className="px-6 py-4">
                        État
                      </th>
                      <th scope="col" className="px-6 py-4">
                        Clients
                      </th>
                      <th scope="col" className="px-6 py-4 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {donnees.items.map((compte) => {
                      const estMoi = compte.id === moi;
                      return (
                        <tr
                          key={compte.id}
                          className={
                            estMoi
                              ? 'bg-red-50/30 hover:bg-red-50/50'
                              : 'hover:bg-slate-50 transition-colors'
                          }
                        >
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              aria-label={`Sélectionner ${compte.email}`}
                              checked={selectionComptes.includes(compte.id)}
                              disabled={estMoi}
                              title={
                                estMoi
                                  ? 'Vous ne pouvez pas sélectionner votre propre compte.'
                                  : undefined
                              }
                              onChange={() =>
                                setSelectionComptes((selection) => basculer(selection, compte.id))
                              }
                              className="w-4 h-4 rounded border-slate-300 cursor-pointer disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">{compte.nom}</span>
                              {estMoi && (
                                <span className="bg-red-100 text-red-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                  Vous
                                </span>
                              )}
                            </div>
                            <span className="block text-xs text-slate-400">{compte.email}</span>
                          </td>
                          <td className="px-6 py-4">
                            <label htmlFor={`role-${compte.id}`} className="sr-only">
                              Rôle de {compte.email}
                            </label>
                            <select
                              id={`role-${compte.id}`}
                              value={compte.role}
                              // Sur SA propre ligne, l'action est désactivée : le serveur la
                              // refuse (400), autant ne pas proposer une porte fermée.
                              disabled={enCours === compte.id || estMoi}
                              title={
                                estMoi
                                  ? 'Vous ne pouvez pas modifier votre propre rôle.'
                                  : undefined
                              }
                              onChange={(evenement) =>
                                void ecrire(
                                  compte,
                                  { role: evenement.target.value as Role },
                                  'Rôle modifié',
                                )
                              }
                              className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                              {ROLES.map((role) => (
                                <option key={role.valeur} value={role.valeur}>
                                  {role.libelle}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={
                                compte.actif
                                  ? 'spill bg-emerald-50 text-emerald-700'
                                  : 'spill bg-slate-100 text-slate-600'
                              }
                            >
                              {compte.actif ? 'Actif' : 'Suspendu'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-slate-600 mono">{compte.clients}</td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <Link
                              to={`/journal?cible=${encodeURIComponent(compte.email)}`}
                              title="Ouvre le journal filtré sur ce compte : qui a fait quoi, et quand."
                              className="mr-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50"
                            >
                              Historique
                            </Link>
                            <button
                              type="button"
                              disabled={enCours === compte.id}
                              onClick={() => void reinitialiser(compte)}
                              title="Fabrique un lien à usage unique pour que ce compte choisisse un nouveau mot de passe."
                              className="mr-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50 disabled:opacity-60"
                            >
                              Réinitialiser
                            </button>
                            <button
                              type="button"
                              disabled={enCours === compte.id || estMoi}
                              title={
                                estMoi
                                  ? 'Vous ne pouvez pas archiver votre propre compte.'
                                  : 'Archive le compte : il sort des listes et reste restaurable pendant la rétention.'
                              }
                              onClick={() => void archiver(compte)}
                              className="px-3 py-2 bg-white border border-red-200 text-red-600 text-xs rounded-lg hover:bg-red-50 disabled:opacity-60"
                            >
                              Suspendre
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {onglet === 'invitations' && (
        <div
          id="panneau-invitations"
          role="tabpanel"
          aria-labelledby="onglet-invitations"
          className="space-y-4"
        >
          {selectionInvitations.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <p className="text-sm text-blue-800 font-medium">
                {accorder(
                  selectionInvitations.length,
                  'invitation sélectionnée',
                  'invitations sélectionnées',
                )}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectionInvitations([])}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50"
                >
                  Tout désélectionner
                </button>
                <button
                  type="button"
                  onClick={() => void revoquerSelection()}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
                >
                  Révoquer la sélection
                </button>
              </div>
            </div>
          )}

          {erreurInvitation && (
            <p role="alert" className="text-sm text-red-700">
              {erreurInvitation}
            </p>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {invitations.length === 0 ? (
              <p className="p-6 text-sm text-slate-600">
                Aucune invitation en attente. L’accès se fait uniquement sur invitation.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <caption className="sr-only">
                    Invitations créées, avec leur état et leur échéance
                  </caption>
                  <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                    <tr>
                      <th scope="col" className="px-6 py-3 w-12">
                        <input
                          type="checkbox"
                          aria-label="Sélectionner toutes les invitations en attente"
                          checked={
                            cochablesInvitations.length > 0 &&
                            cochablesInvitations.every((invitation) =>
                              selectionInvitations.includes(invitation.id),
                            )
                          }
                          onChange={(evenement) =>
                            setSelectionInvitations(
                              evenement.target.checked
                                ? cochablesInvitations.map((invitation) => invitation.id)
                                : [],
                            )
                          }
                          className="w-4 h-4 rounded border-slate-300 cursor-pointer"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Technicien
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Rôle
                      </th>
                      <th scope="col" className="px-6 py-3">
                        État
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Échéance
                      </th>
                      <th scope="col" className="px-6 py-3 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {invitations.map((invitation) => (
                      <tr key={invitation.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-3">
                          <input
                            type="checkbox"
                            aria-label={`Sélectionner l’invitation de ${invitation.email}`}
                            checked={selectionInvitations.includes(invitation.id)}
                            onChange={() =>
                              setSelectionInvitations((selection) =>
                                basculer(selection, invitation.id),
                              )
                            }
                            className="w-4 h-4 rounded border-slate-300 cursor-pointer"
                          />
                        </td>
                        <td className="px-6 py-3">
                          <span className="font-medium text-slate-800">{invitation.nom}</span>
                          <span className="block text-xs text-slate-400">{invitation.email}</span>
                        </td>
                        <td className="px-6 py-3 text-slate-600">{invitation.roleLibelle}</td>
                        <td className="px-6 py-3">
                          <span
                            className={
                              invitation.valide
                                ? 'spill bg-amber-50 text-amber-800'
                                : 'spill bg-slate-100 text-slate-600'
                            }
                          >
                            {invitation.utiliseLe
                              ? 'Servie'
                              : invitation.perimee
                                ? 'Périmée'
                                : 'En attente'}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-slate-600 mono">
                          {invitation.expireLe.slice(0, 10)}
                        </td>
                        <td className="px-6 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            disabled={enCours === invitation.id}
                            title="Fabrique un nouveau lien : l’ancien cesse aussitôt de fonctionner."
                            onClick={() => void renvoyer(invitation)}
                            className="mr-2 inline-flex items-center gap-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50 disabled:opacity-40"
                          >
                            <Mail aria-hidden="true" className="w-3.5 h-3.5" />
                            Renvoyer
                          </button>
                          <button
                            type="button"
                            disabled={enCours === invitation.id}
                            title="Supprime l’invitation : le lien cesse de fonctionner."
                            onClick={() => void revoquer(invitation)}
                            className="px-3 py-2 bg-white border border-red-200 text-red-600 text-xs rounded-lg hover:bg-red-50 disabled:opacity-40"
                          >
                            Révoquer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {onglet === 'invitations-servies' && (
        <div
          id="panneau-invitations-servies"
          role="tabpanel"
          aria-labelledby="onglet-invitations-servies"
          className="space-y-4"
        >
          <p className="text-sm text-slate-600">
            Invitations closes : servies, donc devenues des comptes, ou périmées sans avoir servi.
            Elles ne se renvoient ni ne se révoquent — une invitation servie appartient à
            l’histoire.
          </p>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {invitationsServies.length === 0 ? (
              <p className="p-6 text-sm text-slate-600">Aucune invitation close pour l’instant.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <caption className="sr-only">
                    Invitations closes, servies ou périmées, avec leur échéance
                  </caption>
                  <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Technicien
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Rôle
                      </th>
                      <th scope="col" className="px-6 py-3">
                        État
                      </th>
                      <th scope="col" className="px-6 py-3 whitespace-nowrap">
                        Échéance
                      </th>
                      <th scope="col" className="px-6 py-3 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {invitationsServies.map((invitation) => (
                      <tr key={invitation.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-3">
                          <span className="font-medium text-slate-800">{invitation.nom}</span>
                          <span className="block text-xs text-slate-400">{invitation.email}</span>
                        </td>
                        <td className="px-6 py-3 text-slate-600 whitespace-nowrap">
                          {invitation.roleLibelle}
                        </td>
                        <td className="px-6 py-3">
                          <span className="spill bg-slate-100 text-slate-600">
                            {invitation.utiliseLe ? 'Servie' : 'Périmée'}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-slate-600 mono whitespace-nowrap">
                          {invitation.expireLe.slice(0, 10)}
                        </td>
                        <td className="px-6 py-3 text-right whitespace-nowrap">
                          {/* Une invitation PÉRIMÉE se renvoie ; une invitation
                              SERVIE appartient à l'histoire et ne se rejoue pas. */}
                          {invitation.utiliseLe ? (
                            <span className="text-xs text-slate-400">Servie : rien à renvoyer</span>
                          ) : (
                            <button
                              type="button"
                              disabled={enCours === invitation.id}
                              onClick={() => void renvoyer(invitation)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50 disabled:opacity-40"
                            >
                              <Mail aria-hidden="true" className="w-3.5 h-3.5" />
                              Renvoyer
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {onglet === 'archives' && (
        <div
          id="panneau-archives"
          role="tabpanel"
          aria-labelledby="onglet-archives"
          className="space-y-4"
        >
          <p className="text-sm text-slate-600">
            Rétention de <strong>{retentionTexte}</strong> : un compte archivé garde son
            portefeuille et se restaure d’un clic jusque-là.
          </p>

          {echues > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-4">
              <p className="text-sm text-amber-900">
                {accorder(
                  echues,
                  'archive a dépassé la rétention',
                  'archives ont dépassé la rétention',
                )}
                . La purge efface<strong> définitivement</strong> ce compte et son portefeuille.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                {purgeArmee ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setPurgeArmee(false)}
                      className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      disabled={enCours === 'purge'}
                      onClick={() => void purger()}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 border border-red-600 rounded hover:bg-red-700 disabled:opacity-60"
                    >
                      {enCours === 'purge' ? 'Purge…' : `Confirmer la purge (${echues})`}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPurgeArmee(true)}
                    className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
                  >
                    Purger définitivement…
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {!archives || archives.total === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
                  <Archive aria-hidden="true" className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-base font-semibold text-slate-800 mb-1">
                  Aucun compte archivé
                </h3>
                <p className="text-sm text-slate-500 text-center max-w-md">
                  Les comptes suspendus apparaîtront ici pour une période de rétention de{' '}
                  {retentionTexte}.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <caption className="sr-only">
                    Comptes archivés, avec leur date d’archivage et la rétention restante
                  </caption>
                  <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Compte
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Rôle
                      </th>
                      <th scope="col" className="px-6 py-3 whitespace-nowrap">
                        Archivé le
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Clients
                      </th>
                      <th scope="col" className="px-6 py-3 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {archives.items.map((compte) => {
                      const echu = compte.echue === true;
                      return (
                        <tr key={compte.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-3">
                            <span className="font-medium text-slate-800">{compte.nom}</span>
                            <span className="block text-xs text-slate-400">{compte.email}</span>
                          </td>
                          <td className="px-6 py-3 text-slate-600 whitespace-nowrap">
                            {compte.roleLibelle}
                          </td>
                          <td className="px-6 py-3 text-slate-600 mono whitespace-nowrap">
                            {compte.archiveLe?.slice(0, 10) ?? '—'}
                            {echu && (
                              <span className="ml-2 spill bg-amber-50 text-amber-800">échue</span>
                            )}
                          </td>
                          <td className="px-6 py-3 text-slate-600 mono">{compte.clients}</td>
                          <td className="px-6 py-3 text-right">
                            <button
                              type="button"
                              disabled={enCours === compte.id}
                              title="Rend le compte à la vie courante : il redevient actif."
                              onClick={() => void restaurer(compte)}
                              className="px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50 disabled:opacity-60"
                            >
                              Restaurer
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {techniciens.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <h2 className="px-6 py-4 text-sm font-semibold text-slate-800 border-b border-slate-100">
            Techniciens ({techniciens.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3 text-left">Technicien</th>
                  <th className="px-6 py-3 text-left">Rôle</th>
                  <th className="px-6 py-3 text-left">Habilitations</th>
                  <th className="px-6 py-3 text-left">Compte</th>
                  <th className="px-6 py-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {techniciens.map((fiche) => (
                  <tr key={fiche.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-800">{fiche.nom}</p>
                      <p className="text-xs text-slate-500">{fiche.email}</p>
                      {fiche.societe && <p className="text-xs text-slate-400">{fiche.societe}</p>}
                    </td>
                    <td className="px-6 py-4 text-slate-700">{fiche.roleLibelle}</td>
                    <td className="px-6 py-4 text-slate-700">
                      {fiche.nombreHabilitations === 0
                        ? '—'
                        : fiche.habilitations
                            .map((h) => (h.categorie ? `${h.libelle} (${h.categorie})` : h.libelle))
                            .join(', ')}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {/* L'état vient du SERVEUR : une fiche dont le compte est
                          archivé ne peut plus annoncer un compte ouvert. */}
                      {fiche.compteEtatLibelle ?? (fiche.compteId ? 'Compte ouvert' : '—')}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={
                          fiche.actif
                            ? 'spill bg-emerald-50 text-emerald-700'
                            : 'spill bg-slate-100 text-slate-600'
                        }
                      >
                        {fiche.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
